import {
  delay,
  call,
  race,
  fork,
  put,
  take,
  select,
  debounce,
  takeEvery,
} from "redux-saga/effects";
import i18n from "i18n";

import history from "components/common/navigation/AppHistory";

import { getUserPayload } from "utils/data/User";
import auth from "utils/Auth";
import { getFiltersIndexMedias, hasPermission } from "utils/data/Content";
import { fetchIndexMedias } from "api/legacy/ContentAPI";
import {
  GET_LANGUAGES,
  GET_COUNTRIES,
  GET_PROJECTS,
  GET_OFFERS,
  GET_METADATAS,
  GET_CASTINGS,
  GET_CONTENTS,
  GET_SMARTLISTS,
  GET_COMPONENTS,
  CREATE_CONTENT,
  UPDATE_CONTENT,
  UPLOAD_IMAGE_CONTENT,
  CREATE_CASTING,
  CREATE_PROJECT,
  UPDATE_PROJECT,
  GET_ORGA_CONFIG,
  GET_EDUS,
  CREATE_EDU,
  UPDATE_EDU,
  GET_USERS,
  CREATE_USER,
  UPDATE_USER,
  UPDATE_COMPONENT,
  CREATE_COMPONENT,
  DELETE_COMPONENT,
  GET_PLANS,
  GET_PLANS_OPTIONS,
  PAYEMENT_PROCESS,
  VALIDATE_2FA,
  DISABLE_2FA,
  GET_USER_PROFILE,
  LOGIN_REQUEST,
  LOGOUT_REQUEST,
  SENDING_LOGIN_REQUEST,
  SET_AUTH,
  SET_EDITOR_AUTH,
  STATUSES,
  SET_PROJECT,
  SET_PAGE,
  DELETE_EDU,
  DELETE_USER,
  UPDATE_PROFILE,
} from "../actions/ActionTypes";

import {
  setProjectUuid,
  setProject,
  resetProject,
} from "../actions/architecture";
import { fetchApi, clearData } from "../actions/FetchActions";
import { postApi } from "../actions/SendActions";
import { notifError } from "../actions/FlashMessageActions";
import { setProjectLanguages } from "../actions/languages";

function* sendNotif(message) {
  yield put(notifError(message));
}

/**
 * Login API call
 */
function* manageAuthentication(type, token, params) {
  let response;

  yield put({ type: SENDING_LOGIN_REQUEST, isFetching: true });
  switch (type) {
    case "login":
      response = yield call(auth.login, params.email, params.password, token);
      break;
    case "confirm_login":
      response = yield call(
        auth.confirmLogin,
        params.authentication_data,
        params.otp_token,
        token,
      );
      break;
    case "register":
      response = yield call(
        auth.register,
        params.email,
        params.password,
        params.first_name,
        params.last_name,
        token,
      );
      break;
    case "logout":
      response = yield call(auth.logout, token);
      break;
    default:
      break;
  }

  yield put({ type: SENDING_LOGIN_REQUEST, isFetching: false });

  const typeError =
    response.error || (response.errorDetails && response.errorDetails.status);

  if (typeError) {
    switch (typeError) {
      case "unauthorized":
        yield fork(sendNotif, i18n.t("login:badUsernameOrPassword"));
        break;
      case "customError":
        yield fork(sendNotif, i18n.t("login:tooManySessions"));
        break;
      case "token_invalid":
        yield fork(sendNotif, i18n.t("login:token_invalid"));
        break;
      case "token_expired":
        yield fork(sendNotif, i18n.t("login:token_expired"));
        break;
      default:
        yield fork(sendNotif, i18n.t("error:genericError"));
        break;
    }
    return false;
  }

  return response;
}

/**
 * Login flow
 */
function* loginFlow() {
  while (true) {
    const request = yield take(LOGIN_REQUEST);
    const { email, password, otp_token, authentication_data } = request.data;
    let winner = null;

    if (!otp_token && !authentication_data) {
      winner = yield race({
        auth: call(manageAuthentication, "login", request.token, {
          email,
          password,
        }),
        logout: take(LOGOUT_REQUEST),
      });
    } else {
      winner = yield race({
        auth: call(manageAuthentication, "confirm_login", "", {
          otp_token,
          authentication_data,
        }),
        logout: take(LOGOUT_REQUEST),
      });
    }

    if (winner.auth && !winner.auth.authentication) {
      yield put({ type: SET_AUTH, loggedIn: true });

      if (
        winner.auth.organization_uuid &&
        winner.auth.organization_uuid.length > 0 &&
        winner.auth.orga_enabled
      ) {
        yield put({
          type: SET_EDITOR_AUTH,
          editorAuth: true,
          organization_uuid: winner.auth.organization_uuid,
        });
      }

      yield put({
        type: GET_USER_PROFILE,
        status: STATUSES.SUCCESSFUL,
        params: winner.auth,
      });
      yield put(setProject({ project: {}, projectSlug: "" }));
      yield put(setProjectUuid(""));
      history.push(request.redirectTo);
    } else if (winner.auth && winner.auth.authentication) {
      yield put({
        type: GET_USER_PROFILE,
        status: STATUSES.SUCCESSFUL,
        params: winner.auth,
      });
    } else if (winner.logout) {
      yield put({ type: SET_AUTH, loggedIn: false });
      yield put({ type: SET_EDITOR_AUTH, editorAuth: false });
      yield call(manageAuthentication, "logout", request.token, {});
      yield call(resetAllDatas);
      yield put(clearData("projects"));
      yield put(resetProject());
    }
  }
}

/**
 * Logout flow
 */
function* logoutFlow() {
  while (true) {
    const action = yield take(LOGOUT_REQUEST);
    const success = yield call(
      manageAuthentication,
      "logout",
      action.token,
      {},
    );

    if (success) {
      yield put({
        type: SET_AUTH,
        loggedIn: false,
        message: action.message,
      });
      yield put({ type: SET_EDITOR_AUTH, editorAuth: false });
      yield put({
        type: GET_USER_PROFILE,
        status: STATUSES.SUCCESSFUL,
        params: {},
      });
      history.push(action.redirectTo);
      yield call(resetAllDatas);
      yield put(clearData("projects"));
      yield put(resetProject());
    }
  }
}

/**
 * Payment flow
 */
function* paymentFlow() {
  while (true) {
    const action = yield take(PAYEMENT_PROCESS);
    if (action.status === STATUSES.SUCCESSFUL) {
      const { orga_enabled, organization_uuid } = action.params;
      if (organization_uuid && organization_uuid.length > 0 && orga_enabled) {
        yield put({
          type: SET_EDITOR_AUTH,
          editorAuth: true,
          organization_uuid,
        });
      }
    }
  }
}

/**
 * Fetch the API endpoints to refresh the data
 */
function* refreshData() {
  while (true) {
    const token = auth.getToken();
    if (token && token.length > 0) {
      yield put(
        fetchApi({
          endpoint: "/api/user/v2/session",
          type: GET_USER_PROFILE,
          token,
        }),
      );
    }
    yield delay(30000);
  }
}

/**
 * Fetch the initial data
 */
function* getInitialData() {
  const token = auth.getToken();

  if (token && token.length > 0) {
    yield call(fetchOrgaData);
    yield call(initialDataPayloads, token);
  }

  yield call(getCountriesData);
  yield call(getPlansData);
}

function* putProjectLanguages(action) {
  if (action.status === STATUSES.SUCCESSFUL) {
    const currentLanguageSlug = i18n.language;
    yield put(setProjectLanguages({ currentLanguageSlug, languages: action }));
  }
}

function* getLoggedInData(action) {
  if (action.loggedIn) {
    const token = auth.getToken();

    yield call(fetchOrgaData);
    yield call(initialDataPayloads, token);
  }
}

function* getPlansData() {
  yield put(
    postApi({
      endpoint: "/api/user/v2/admin/plans",
      type: GET_PLANS,
      item: "plans",
    }),
  );

  yield put(
    postApi({
      endpoint: "/api/user/v2/admin/plan_options",
      type: GET_PLANS_OPTIONS,
      item: "plan_options",
      data: {
        indexes: ["uuid", "type"],
      },
    }),
  );
}

function* getCountriesData() {
  yield put(
    fetchApi({
      endpoint: "/api/project/v4/countries",
      type: GET_COUNTRIES,
      item: "countries",
    }),
  );
}

function* initialDataPayloads(token) {
  yield put(
    fetchApi({
      endpoint: "/api/project/v4/admin/storefront_projects",
      type: GET_PROJECTS,
      item: "projects",
      token,
    }),
  );

  yield put(
    fetchApi({
      endpoint: "/api/project/v4/admin/languages",
      type: GET_LANGUAGES,
      item: "languages",
      token,
    }),
  );

  yield put(
    fetchApi({
      endpoint: "/api/media/v6/admin/medias/metadata",
      type: GET_METADATAS,
      item: "metadatas",
      token,
    }),
  );
}

/**
 * Force Fetch all project data
 */
function* getProjectData({ abilities, projectSlug }) {
  yield call(resetAllDatas);
  yield call(fetchProjectDataPayloads, { abilities, projectSlug });
}

/**
 * Force Fetch all data after change project
 */
function* redirectAfterSetProject() {
  while (true) {
    const action = yield take(SET_PROJECT);
    const { project, projectSlug } = action;

    yield call(getProjectData, {
      abilities: project?.abilities,
      projectSlug: projectSlug,
    });
  }
}

/**
 * Force Fetch all data after change project
 */
function* refreshAfterAddEDU() {
  while (true) {
    const action = yield take([CREATE_EDU, UPDATE_EDU, DELETE_EDU]);
    if (action.status === STATUSES.SUCCESSFUL) {
      const token = auth.getToken();

      if (token && token.length > 0) {
        yield put(
          postApi({
            endpoint: "/api/user/v3/admin/users/list_users",
            type: GET_EDUS,
            item: "edus",
            token,
            headers: action.headers,
            data: {
              role: "edu",
            },
          }),
        );
      }
    }
  }
}

/**
 * Force Fetch all data after create user
 */
function* refreshAfterAddUser() {
  while (true) {
    const action = yield take([CREATE_USER, UPDATE_USER, DELETE_USER]);
    if (action.status === STATUSES.SUCCESSFUL) {
      const token = auth.getToken();

      if (token && token.length > 0) {
        yield put(
          postApi({
            endpoint: "/api/user/v3/admin/users/list_users",
            type: GET_USERS,
            item: "users",
            token,
            headers: action.headers,
            data: {
              role: "standard",
            },
          }),
        );
      }
    }
  }
}

function* debounceAfterUpdateComponent() {
  // debounce by 500ms
  yield debounce(500, UPDATE_COMPONENT, refreshComponents);
}

function* refreshComponents(action) {
  if (action.status === STATUSES.SUCCESSFUL) {
    const token = auth.getToken();

    if (token && token.length > 0) {
      yield put(
        postApi({
          endpoint: "/api/project/v3/admin/components",
          type: GET_COMPONENTS,
          item: "components",
          token,
          headers: action.headers,
          data: {
            indexes: ["uuid", "section", "platform"],
            nested: {
              dynamic_component: {},
              static_component: {},
            },
            page: 1,
            per_page: 10000,
          },
        }),
      );
    }
  }
}

/**
 * Force Fetch all data after create / update component
 */
function* refreshAfterAddComponent() {
  while (true) {
    const action = yield take([CREATE_COMPONENT, DELETE_COMPONENT]);
    yield call(refreshComponents, action);
  }
}

function* fetchProjectDataPayloads({ abilities, projectSlug }) {
  const token = auth.getToken();

  if (token && token.length > 0 && projectSlug) {
    if (hasPermission(abilities, "offers", "offers")) {
      yield put(
        fetchApi({
          endpoint: "/api/offer/v5/admin/offers",
          type: GET_OFFERS,
          item: "offers",
          token,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          data: {
            page: 1,
            per_page: 100000,
          },
        }),
      );
    }

    yield put(
      fetchApi({
        endpoint: "/api/media/v6/admin/medias",
        type: GET_CONTENTS,
        item: "contents",
        token,
      }),
    );

    yield put(
      postApi({
        endpoint: "/api/smartlist/v3/admin/smartlists",
        type: GET_SMARTLISTS,
        item: "smartlists",
        token,
        headers: [
          {
            key: "xx-ok-project",
            value: projectSlug,
          },
        ],
        data: {
          nested: {
            smartlist_items: {},
            countries: {},
            translations: {},
            custom_params: {},
            comments: {},
          },
          page: 1,
          per_page: 100000,
        },
      }),
    );

    if (hasPermission(abilities, "analytics", "users")) {
      yield put(
        postApi({
          endpoint: "/api/user/v3/admin/users/list_users",
          type: GET_EDUS,
          item: "edus",
          token,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          data: {
            role: "edu",
          },
        }),
      );
    }

    if (
      hasPermission(abilities, "platform", "layout") ||
      hasPermission(abilities, "platform", "configuration")
    ) {
      yield put(
        postApi({
          endpoint: "/api/project/v3/admin/components",
          type: GET_COMPONENTS,
          item: "components",
          token,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          data: {
            indexes: ["uuid", "section", "platform"],
            nested: {
              dynamic_component: {},
              static_component: {},
            },
            page: 1,
            per_page: 10000,
          },
        }),
      );
    }
  }
}

/**
 * Force Fetch Projects after CREATE OR UPDATE
 */
function* refreshProjects() {
  while (true) {
    const action = yield take([CREATE_PROJECT, UPDATE_PROJECT]);
    if (action.status === STATUSES.SUCCESSFUL) {
      const token = auth.getToken();

      if (token && token.length > 0) {
        yield put(
          fetchApi({
            endpoint: "/api/project/v4/admin/storefront_projects",
            type: GET_PROJECTS,
            item: "projects",
            token,
          }),
        );
      }
    }
  }
}

/**
 * Force Fetch Contents after CREATE OR UPDATE
 */
function* refreshContents() {
  while (true) {
    const action = yield take([
      CREATE_CONTENT,
      UPDATE_CONTENT,
      UPLOAD_IMAGE_CONTENT,
    ]);

    if (action.status === STATUSES.SUCCESSFUL) {
      const token = auth.getToken();

      if (token && token.length > 0) {
        yield put(
          fetchApi({
            endpoint: "/api/media/v6/admin/medias",
            type: GET_CONTENTS,
            item: "contents",
            token,
          }),
        );
      }
    }
  }
}

/**
 * Refetch index medias when ListNavigationV2 changes page
 */
function* refreshIndexMedias() {
  while (true) {
    const action = yield take([SET_PAGE]);

    if (action.item === "index_medias") {
      const token = auth.getToken();

      if (token && token.length > 0) {
        const getIndexMedias = (state) => state.fetchReducer?.index_medias;

        const indexMedias = yield select(getIndexMedias);
        const data = getFiltersIndexMedias(indexMedias);

        yield put(fetchIndexMedias(token, data));
      }
    }
  }
}

function* fetchOrgaData() {
  const token = auth.getToken();

  if (token && token.length > 0) {
    yield put(
      fetchApi({
        endpoint: "/api/project/v4/admin/organization_params",
        type: GET_ORGA_CONFIG,
        item: "orga_config",
        token,
      }),
    );
  }
}

/**
 * Refresh user data when user logs in
 */
function* refreshUser() {
  // Initial load if isLoggedIn
  if (auth.loggedIn()) {
    const token = auth.getToken();
    yield put(fetchApi(getUserPayload(token)));
  }

  while (true) {
    const action = yield take([
      SET_AUTH,
      VALIDATE_2FA,
      DISABLE_2FA,
      UPDATE_PROFILE,
    ]);
    if (action.type === SET_AUTH && !action.loggedIn) {
      yield put(clearData("user"));
    } else if (
      action.type === SET_AUTH ||
      action.status === STATUSES.SUCCESSFUL
    ) {
      const token = auth.getToken();
      yield put(fetchApi(getUserPayload(token)));
    }
  }
}

function* resetAllDatas() {
  yield put(clearData("components"));
  yield put(clearData("contents"));
  yield put(clearData("deployments"));
  yield put(clearData("free_copies"));
  yield put(clearData("gifts"));
  yield put(clearData("offers"));
  yield put(clearData("smartlists"));
  yield put(clearData("source_medias"));
  yield put(clearData("hosted_streams"));
  yield put(clearData("edus"));
  yield put(clearData("users"));
  yield put(clearData("ongoing_lives"));
  yield put(clearData("comments"));
}

export default function* root() {
  yield fork(loginFlow);
  yield fork(logoutFlow);
  yield fork(paymentFlow);
  yield fork(refreshData);
  yield fork(getInitialData);
  yield fork(refreshProjects);
  yield fork(refreshContents);
  yield fork(refreshAfterAddEDU);
  yield fork(refreshAfterAddUser);
  yield fork(redirectAfterSetProject);
  yield fork(refreshAfterAddComponent);
  yield fork(refreshUser);
  yield fork(refreshIndexMedias);
  yield fork(debounceAfterUpdateComponent);
  yield takeEvery(SET_AUTH, getLoggedInData);
  yield takeEvery(GET_LANGUAGES, putProjectLanguages);
}
